<script setup lang="ts">
import introVideo from '@/assets/animations/home-intro.mp4';
import introPlaceholder from '@/assets/images/home-intro-placeholder.jpeg';

const { isMobile } = useWindowResize();
</script>

<template>
	<div class="page home-page-container">
		<section class="banner-section">
			<div class="content-wrapper">
				<div class="content-inner">
					<div class="banner-content">
						<h1 class="text-semibold no-select">
							Your gateway to <span class="text-highlight">Japanese</span><br />
							experiences worldwide.
						</h1>
						<p class="text-regular no-select">
							Join the millions of people who discover, attend and host amazing events with Peatix.
						</p>
					</div>
					<div class="cta-container">
						<RouterLink class="cta-btn" :to="{ name: ROUTE.VISIT_JAPAN.NAME }">
							<BaseButton
								:variant="BtnVariant[1]"
								:color="BtnColor[1]"
								:size="isMobile ? BtnSize[2] : BtnSize[4]"
							>
								Find Japanese events
							</BaseButton>
						</RouterLink>
						<RouterLink class="cta-btn" :to="{ name: ROUTE.EVENT_CREATE.NAME }">
							<BaseButton
								:variant="BtnVariant[1]"
								:color="BtnColor[3]"
								:size="isMobile ? BtnSize[2] : BtnSize[4]"
							>
								Create your event
							</BaseButton>
						</RouterLink>
					</div>
				</div>
			</div>
			<video id="intro-video" autoplay loop muted playsinline :poster="introPlaceholder">
				<source :src="introVideo" type="video/mp4" />
				Your browser does not support HTML5 video.
			</video>
		</section>

		<!-- Japan events discovery -->
		<JapanEventsSection />

		<!-- Event creation introduction -->
		<EventCreationSection />

		<!-- Get started -->
		<GetStartedSection>
			<template #button>
				<RouterLink :to="{ name: ROUTE.EVENT_CREATE.NAME }">
					<BaseButton :size="BtnSize[3]"> Create an event </BaseButton>
				</RouterLink>
			</template>
		</GetStartedSection>
	</div>
</template>

<style scoped lang="scss">
.home-page-container {
	text-align: center;
	max-width: none;
	margin: 0;
	padding: 0;

	.title {
		color: colors-get(gray, 900);
	}

	.sub-title {
		color: colors-get(gray, 600);
	}
}

section {
	padding-top: spacings-get(12);
	text-align: center;

	.section-inner {
		max-width: rem(1008);
		padding: 0 spacings-get(4);
		margin: 0 auto;

		> .title {
			margin-bottom: spacings-get(5);
			@include fonts-size(display-md);

			@include media-query-max(mobile) {
				@include fonts-size(display-sm);
			}
		}

		> .sub-title {
			margin-bottom: spacings-get(12);
			@include fonts-size(text-xl);

			@include media-query-max(mobile) {
				@include fonts-size(text-md);
			}
		}
	}

	.introduction {
		display: grid;
		grid-template-columns: 40% auto;
		padding-bottom: spacings-get(12);

		@include media-query-max(mobile) {
			grid-template-columns: 1fr;
		}

		&.event-creation {
			gap: rem(72);

			@include media-query-max(mobile) {
				gap: spacings-get(10);
			}
		}

		&.profile {
			gap: spacings-get(12);
		}

		.preview-image {
			width: 100%;
		}

		.content {
			text-align: start;
			margin: auto;
		}
	}
}

.banner-section {
	position: relative;
	padding: 0;
	height: rem(600);
	background: colors-get(base, black);

	@include media-query-max(mobile) {
		height: rem(600);
	}

	#intro-video {
		position: absolute;
		left: 0;
		top: 0;
		width: 100%;
		height: 100%;
		z-index: 1;
		object-position: center;
		object-fit: cover;
		opacity: 0.5;
	}

	.content-wrapper {
		position: absolute;
		display: grid;
		align-items: center;
		width: 100%;
		height: 100%;
		color: colors-get(base, white);
		z-index: 2;

		.content-inner {
			display: flex;
			flex-direction: column;
			gap: spacings-get(6);
			padding: 0 spacings-get(4);
		}
	}

	.banner-content {
		h1 {
			margin-bottom: spacings-get(4);
			@include fonts-size(display-lg);

			@include media-query-max(mobile) {
				margin-top: rem(100);
				@include fonts-size(display-md);
			}
		}

		p {
			width: rem(612);
			margin: 0 auto;
			@include fonts-size(text-xl);

			@include media-query-max(mobile) {
				width: rem(340);
				@include fonts-size(text-md);
			}
		}
	}

	.cta-container {
		display: flex;
		flex-direction: row;
		justify-content: center;
		align-items: center;
		gap: spacings-get(4);

		@include media-query-max(mobile) {
			flex-direction: column;
			gap: spacings-get(6);
		}
	}

	.cta-btn {
		width: fit-content;
	}
}
</style>
